import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { useTranslation } from 'react-i18next';
import { OnboardingSkipSetupDropdown } from './OnboardingSkipSetupDropdown';
import { OnboardingStepper } from './OnboardingStepper';
import type { OnboardingStep6Phase, OnboardingWizardView } from './OnboardingWizard.types';
import { ATLAS_LENS_COMPONENT_VARS } from '../Form/atlasLensTokens';
import { atlasBorderTopDivider, atlasSpinnerSizeSm } from '../Form/atlasLensStyles';
import { useOnboardingLens } from './onboardingLens';

interface OnboardingWizardFooterProps {
  activeStep: number;
  view: OnboardingWizardView;
  step6Phase: OnboardingStep6Phase | null;
  step6SubmittingAction?: 'finish' | 'skip' | null;
  isMobile: boolean;
  canContinue: boolean;
  reassignCanSubmit?: boolean;
  isReassignSubmitting?: boolean;
  isSkipSubmitting?: boolean;
  onBack: () => void;
  onContinue: () => void;
  onDoThisLater?: () => void;
  onGoToCourses?: () => void;
  onRetrySubmit?: () => void;
  onReassignDone?: () => void;
  onSkipDoLater?: () => void;
  onSkipNotMyTask?: () => void;
  assignToColleagueDisabled?: boolean;
  onSetupWithAi?: () => void;
  onSetupWithoutAi?: () => void;
  stepperCurrent?: number;
  stepperTotal?: number;
  /** When true, success CTA uses training-plan copy (AI path). */
  hasTrainingPlan?: boolean;
}

/** Figma footer — dot stepper layout (16692:5494) used on all wizard steps. */
export function OnboardingWizardFooter({
  activeStep,
  view,
  step6Phase,
  step6SubmittingAction = null,
  isMobile,
  canContinue,
  reassignCanSubmit = false,
  isReassignSubmitting = false,
  isSkipSubmitting = false,
  stepperCurrent,
  stepperTotal,
  onBack,
  onContinue,
  onDoThisLater,
  onGoToCourses,
  onRetrySubmit,
  onReassignDone,
  onSkipDoLater,
  onSkipNotMyTask,
  assignToColleagueDisabled = false,
  onSetupWithAi,
  onSetupWithoutAi,
  hasTrainingPlan = false,
}: OnboardingWizardFooterProps) {
  const { t } = useTranslation();
  const theme = useTheme() as Theme & LensTheme;
  const { layout, typography, typeDefault, divider, surface, outlineDefault, focusRing } =
    useOnboardingLens();
  const { footer } = layout;
  const step1Footer = layout.welcomeStep1.footer;

  const isStep1 = activeStep === 1 && view === 'steps';
  const isReassignView = view === 'reassign';
  const isStep6Submitting = activeStep === 6 && step6Phase === 'submitting';
  const isAiLoading = isStep6Submitting && hasTrainingPlan;
  const showDoThisLater = activeStep === 6 && view === 'steps' && !isAiLoading;
  const showValidationSteps = activeStep >= 1 && activeStep <= 6 && view === 'steps';
  const isFinalStep = activeStep === 7 && view === 'steps';
  const isStep6Error = isFinalStep && step6Phase === 'error';
  const isStep6Success = isFinalStep && step6Phase === 'success';
  const showDotStepper =
    view === 'steps' &&
    !isStep1 &&
    ((activeStep >= 1 && activeStep <= 6) ||
      (isFinalStep && (isStep6Success || isStep6Error)));

  const footerButtonFocusSx = {
    outline: 'none',
    '&:focus': { outline: 'none' },
    '&:focus:not(:focus-visible)': { boxShadow: 'none' },
    '&.Mui-focusVisible': { outline: 'none', boxShadow: focusRing },
  };

  const tertiaryButtonSx = {
    textTransform: 'none' as const,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    height: isStep1 ? step1Footer.buttonHeight : footer.buttonHeight,
    minHeight: isStep1 ? step1Footer.buttonHeight : footer.buttonHeight,
    color: typeDefault,
    borderRadius: isStep1 ? step1Footer.buttonRadius : undefined,
    px: isStep1 ? step1Footer.buttonPaddingX : undefined,
    py: isStep1 ? step1Footer.buttonPaddingY : undefined,
    ...typography.button,
    ...footerButtonFocusSx,
  };

  const actionButtonSx = {
    textTransform: 'none' as const,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    height: isStep1 ? step1Footer.buttonHeight : footer.buttonHeight,
    minHeight: isStep1 ? step1Footer.buttonHeight : footer.buttonHeight,
    borderRadius: isStep1 ? step1Footer.buttonRadius : undefined,
    px: isStep1 ? step1Footer.buttonPaddingX : undefined,
    py: isStep1 ? step1Footer.buttonPaddingY : undefined,
    ...typography.button,
    ...footerButtonFocusSx,
  };

  const leftActions = isReassignView ? (
    <Button
      variant="text"
      color="inherit"
      onClick={onBack}
      disabled={isReassignSubmitting}
      sx={{ ...tertiaryButtonSx, minWidth: footer.backButtonMinWidth }}
    >
      {t('onboarding.wizard.previous')}
    </Button>
  ) : isStep1 && onSkipDoLater && onSkipNotMyTask ? (
    <OnboardingSkipSetupDropdown
      onDoLater={onSkipDoLater}
      onNotMyTask={onSkipNotMyTask}
      disabled={isSkipSubmitting}
      assignToColleagueDisabled={assignToColleagueDisabled}
    />
  ) : isStep6Success || isAiLoading ? null : (
    <Button
      variant="text"
      color="inherit"
      onClick={onBack}
      disabled={isStep6Submitting}
      sx={{ ...tertiaryButtonSx, minWidth: footer.backButtonMinWidth }}
    >
      {t('onboarding.wizard.previous')}
    </Button>
  );

  const rightActions = isReassignView ? (
    <Button
      variant="contained"
      onClick={onReassignDone}
      disabled={!reassignCanSubmit || isReassignSubmitting}
      startIcon={
        isReassignSubmitting ? <CircularProgress size={atlasSpinnerSizeSm(theme)} color="inherit" aria-hidden /> : undefined
      }
      sx={{
        ...actionButtonSx,
        minWidth: footer.reassignDoneMinWidth,
        ...(!reassignCanSubmit || isReassignSubmitting
          ? {
              backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDisabledBackground,
              color: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDisabledColor,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: ATLAS_LENS_COMPONENT_VARS.buttonPrimaryDisabledBackground,
                boxShadow: 'none',
              },
            }
          : {}),
      }}
    >
      {t('onboarding.wizard.reassign.assign')}
    </Button>
  ) : (
    <>
      {showDoThisLater && onDoThisLater ? (
        <Button
          variant="outlined"
          onClick={onDoThisLater}
          disabled={isStep6Submitting}
          startIcon={
            isStep6Submitting && step6SubmittingAction === 'skip' ? (
              <CircularProgress size={atlasSpinnerSizeSm(theme)} color="inherit" aria-hidden />
            ) : undefined
          }
          sx={{
            ...actionButtonSx,
            minWidth: footer.buttonMinWidth,
            color: typeDefault,
            borderColor: outlineDefault,
            '&:hover': {
              borderColor: outlineDefault,
              backgroundColor: 'transparent',
            },
          }}
        >
          {t('onboarding.wizard.step6.skip_for_now')}
        </Button>
      ) : null}
      {isStep6Error && onRetrySubmit ? (
        <Button
          variant="outlined"
          onClick={onRetrySubmit}
          sx={{
            ...actionButtonSx,
            minWidth: footer.buttonMinWidth,
            color: typeDefault,
            borderColor: outlineDefault,
          }}
        >
          {t('onboarding.wizard.step7.retry')}
        </Button>
      ) : null}
      {isStep6Success && onGoToCourses ? (
        <Button
          variant="contained"
          onClick={onGoToCourses}
          sx={{
            ...actionButtonSx,
            minWidth: layout.complete.primaryButtonMinWidth,
          }}
        >
          {t('onboarding.wizard.step7.go_to_courses')}
        </Button>
      ) : isAiLoading ? null : isStep1 && onSetupWithAi && onSetupWithoutAi ? (
        <>
          <Button
            variant="text"
            color="inherit"
            onClick={onSetupWithoutAi}
            disabled={isSkipSubmitting}
            sx={{
              ...tertiaryButtonSx,
              minWidth: step1Footer.setupWithoutAiMinWidth,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {t('onboarding.wizard.step1.setup_without_ai')}
          </Button>
          <Button
            variant="contained"
            onClick={onSetupWithAi}
            disabled={isSkipSubmitting}
            sx={{
              ...actionButtonSx,
              minWidth: step1Footer.setupWithAiMinWidth,
            }}
          >
            {t('onboarding.wizard.step1.setup_with_ai')}
          </Button>
        </>
      ) : isFinalStep ? null : (
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={(showValidationSteps && !canContinue) || isStep6Submitting}
          startIcon={
            isStep6Submitting && step6SubmittingAction === 'finish' ? (
              <CircularProgress size={atlasSpinnerSizeSm(theme)} color="inherit" aria-hidden />
            ) : undefined
          }
          sx={{
            ...actionButtonSx,
            minWidth: activeStep === 6 ? footer.finishButtonMinWidth : footer.buttonMinWidth,
          }}
        >
          {activeStep === 6
            ? t('onboarding.wizard.step6.finish_setup')
            : t('onboarding.wizard.next')}
        </Button>
      )}
    </>
  );

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        isolation: 'isolate',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexShrink: 0,
        minHeight: isStep1 ? step1Footer.height : footer.height,
        height: isStep1 ? step1Footer.height : footer.height,
        maxHeight: isStep1 ? step1Footer.height : footer.height,
        justifyContent: isStep1 ? 'space-between' : undefined,
        px:
          isStep1 && !isMobile
            ? step1Footer.paddingX
            : isMobile
              ? layout.formSection.paddingY
              : footer.paddingX,
        pt: isStep1 ? step1Footer.paddingTop : footer.paddingTop,
        pb: footer.paddingBottom,
        borderTop: atlasBorderTopDivider(theme, divider),
        backgroundColor: surface,
        gap: isStep1 ? step1Footer.rowGap : footer.actionGap,
      }}
    >
      <Box sx={{ flexShrink: 0, zIndex: 1 }}>{leftActions}</Box>

      {isStep1 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: step1Footer.actionGap,
            flexShrink: 0,
            zIndex: 1,
          }}
        >
          {rightActions}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: footer.actionGap,
            minHeight: footer.contentMinHeight,
            zIndex: 1,
          }}
        >
          {rightActions}
        </Box>
      )}

      {showDotStepper ? (
        <Box
          sx={{
            position: 'absolute',
            top: footer.dotStepperTop,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <OnboardingStepper
            activeStep={activeStep}
            stepperCurrent={stepperCurrent}
            stepperTotal={stepperTotal}
          />
        </Box>
      ) : null}
    </Box>
  );
}
